import type { ChapterPlan, InspectorAuthoritativeBeatId, InspectorBeatRepairRequest, Ledger, StoryBible } from '../types';
import { FOUR_BEAT_SEPARATOR } from './fourBeatChapterAssembly.service';
import {
  ChapterCanonPreflightError,
  logChapterCanonInputShapes,
  logChapterCanonNormalizationWarnings,
  normalizeBeatText,
  normalizeCanonInputArray,
  normalizeCharacterNameList,
  type CanonInputShapeDiagnostic,
  type NormalizedCanonEntry,
} from './canonInputNormalization.service';

export type RepeatedRevelationAssessment = 'SUSPECTED' | 'CONFIRMED';

export interface CanonicalKnownFact {
  factId: string;
  statement: string;
  knownBy: string[];
  source: 'resolved_plot' | 'canon_lock' | 'continuity_note';
  sourceChapter?: number;
  sourceRecordId?: string;
  /** @deprecated use statement */
  fact: string;
  /** @deprecated use factId */
  id: string;
}

export interface PlannedRevelation {
  id: string;
  fact: string;
  revealToCharacters: string[];
}

export interface SuspectedRepeatedRevelationFinding {
  assessment: 'SUSPECTED';
  code: 'SUSPECTED_REPEATED_REVELATION';
  factId: string;
  fact: string;
  povCharacter: string;
  suspectedBeatId?: InspectorAuthoritativeBeatId;
  sentence: string;
  reason: string;
  rediscoveryIndicators: string[];
  allowedDiscoverer?: string;
  highConfidence: boolean;
  legitimate: boolean;
}

export interface ConfirmedRepeatedRevelationFinding {
  assessment: 'CONFIRMED';
  code: 'REPEATED_REVELATION';
  factId: string;
  fact: string;
  povCharacter: string;
  beatId: InspectorAuthoritativeBeatId;
  reason: string;
  correctionExplanation: string;
}

const DISCOVERY_VERB_PATTERN =
  /\b(?:discover(?:ed|s|ing)?|realiz(?:ed|es|ing)|identif(?:ied|ies|ying)|understood\s+that|learned\s+that|came\s+to\s+(?:understand|realize|recognise|recognize)|found\s+out\s+that|it\s+was\s+(?:an?|the))\b/i;

const EXPLICIT_FIRST_TIME_PATTERN =
  /\b(?:for\s+the\s+first\s+time|never\s+(?:knew|known|seen|heard)|not\s+until\s+now|until\s+now\s+he\s+had\s+no\s+idea)\b/i;

const STOPWORDS = new Set([
  'that', 'this', 'with', 'from', 'they', 'them', 'their', 'have', 'been', 'were', 'was', 'into', 'about',
  'would', 'could', 'should', 'there', 'where', 'when', 'what', 'which', 'while', 'after', 'before',
]);

function toCanonicalKnownFact(
  entry: NormalizedCanonEntry,
  source: CanonicalKnownFact['source'],
): CanonicalKnownFact {
  return {
    factId: entry.factId,
    statement: entry.statement,
    knownBy: entry.knownBy,
    source,
    sourceChapter: entry.sourceChapter,
    sourceRecordId: entry.sourceRecordId,
    fact: entry.statement,
    id: entry.factId,
  };
}

function tokenizeFact(fact: string): string[] {
  return fact
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length >= 4 && !STOPWORDS.has(token));
}

function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => (typeof sentence === 'string' ? sentence.trim() : ''))
    .filter(Boolean);
}

function resolvePovCharacter(plan?: ChapterPlan): string {
  const raw = plan?.pov || (plan as { povCharacter?: string })?.povCharacter || '';
  return typeof raw === 'string' ? raw.trim() : '';
}

function resolveCharacterFocus(plan?: ChapterPlan): string[] {
  return normalizeCharacterNameList(plan?.characterFocus);
}

function normalizeCanonLocks(raw: StoryBible['canonLocks'] | undefined): string[] {
  if (!raw) return [];
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed ? [trimmed] : [];
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => normalizeBeatText(entry))
    .filter((entry): entry is string => Boolean(entry));
}

export function buildChapterCanonKnowledge(params: {
  ledger: Ledger;
  storyBible?: StoryBible | null;
  plan?: ChapterPlan;
  chapterNumber?: number;
}): {
  knownAtChapterStart: CanonicalKnownFact[];
  newRevelationsAllowed: PlannedRevelation[];
  povCharacter: string;
} {
  const chapterNumber = params.chapterNumber ?? params.plan?.chapterNumber;
  const diagnostics: CanonInputShapeDiagnostic[] = [];
  const warnings: string[] = [];
  const knownAtChapterStart: CanonicalKnownFact[] = [];

  const resolved = normalizeCanonInputArray({
    entries: params.ledger.resolved_plot_points,
    sourceField: 'ledger.resolved_plot_points',
    chapterNumber,
    idPrefix: 'resolved',
  });
  diagnostics.push(resolved.diagnostic);
  warnings.push(...resolved.warnings);
  knownAtChapterStart.push(
    ...resolved.normalized.map((entry) => toCanonicalKnownFact(entry, 'resolved_plot')),
  );

  const canonLocks = normalizeCanonLocks(params.storyBible?.canonLocks);
  const locks = normalizeCanonInputArray({
    entries: canonLocks,
    sourceField: 'storyBible.canonLocks',
    chapterNumber,
    idPrefix: 'lock',
  });
  diagnostics.push(locks.diagnostic);
  warnings.push(...locks.warnings);
  knownAtChapterStart.push(
    ...locks.normalized.map((entry) => toCanonicalKnownFact(entry, 'canon_lock')),
  );

  logChapterCanonInputShapes(diagnostics);
  logChapterCanonNormalizationWarnings(warnings);

  const povCharacter = resolvePovCharacter(params.plan);
  const focusCharacters = resolveCharacterFocus(params.plan);
  const newRevelationsAllowed: PlannedRevelation[] = [];
  let revealIndex = 0;

  for (const beat of params.plan?.requiredBeats || []) {
    const trimmed = normalizeBeatText(beat);
    if (!trimmed) {
      if (beat != null) {
        logChapterCanonNormalizationWarnings([
          `chapterPlan.requiredBeats unsupported runtimeType=${Array.isArray(beat) ? 'array' : typeof beat}`,
        ]);
      }
      continue;
    }
    const isKnown = knownAtChapterStart.some((known) => {
      const tokens = tokenizeFact(known.statement);
      const beatLower = trimmed.toLowerCase();
      return tokens.filter((token) => beatLower.includes(token)).length >= 2;
    });
    if (isKnown) continue;
    const revealToCharacters = focusCharacters.filter(
      (name) => !povCharacter || name.toLowerCase() !== povCharacter.toLowerCase(),
    );
    if (!revealToCharacters.length) continue;
    revealIndex += 1;
    newRevelationsAllowed.push({
      id: `reveal_${revealIndex}`,
      fact: trimmed,
      revealToCharacters,
    });
  }

  return { knownAtChapterStart, newRevelationsAllowed, povCharacter };
}

export { ChapterCanonPreflightError };

export function buildScribeCanonKnowledgeBlock(knowledge: ReturnType<typeof buildChapterCanonKnowledge>): string {
  const knownLines = knowledge.knownAtChapterStart.map((fact) => `- ${fact.statement}`).join('\n') || '- none';
  const revealLines = knowledge.newRevelationsAllowed
    .map((revelation) => `- ${revelation.fact} (first discovery allowed for: ${revelation.revealToCharacters.join(', ')})`)
    .join('\n') || '- none';

  const block = `
ACCEPTED CANON AT CHAPTER START (ALREADY KNOWN — DO NOT REDISCOVER):
${knownLines}

POV CHARACTER: ${knowledge.povCharacter || 'unspecified'}

NEW REVELATIONS ALLOWED THIS CHAPTER:
${revealLines}

REVELATION RULES:
- The POV character may recall, act on, conceal, or reference established facts without treating them as new discoveries.
- Another focused character may discover a fact for the first time only when listed under NEW REVELATIONS ALLOWED.
- Do NOT write the POV character identifying, realizing, or learning an ACCEPTED CANON fact as if encountering it for the first time.
`.trim();

  if (block.includes('[object Object]')) {
    throw new ChapterCanonPreflightError('Canon knowledge prompt contained unsafe object serialization.', {
      sourceField: 'buildScribeCanonKnowledgeBlock',
      chapterNumber: undefined,
      runtimeType: 'string',
    });
  }

  return block;
}

export function buildInspectorCanonKnowledgeBlock(knowledge: ReturnType<typeof buildChapterCanonKnowledge>): string {
  return `${buildScribeCanonKnowledgeBlock(knowledge)}

INSPECTOR REVELATION CHECK:
- Deterministic pre-checks may flag SUSPECTED_REPEATED_REVELATION — treat these as advisory only.
- Return verdict REPAIR_REQUIRED with reasonCodes including REPEATED_REVELATION only when you confirm the POV character rediscovers an ACCEPTED CANON fact.
- PASS legitimate recall, investigation, concealment, explanation to another character, new consequences, or new subsystems within known facts.
- PASS when a non-POV focused character discovers a fact listed under NEW REVELATIONS ALLOWED.
- Do NOT return verdict BLOCKED solely for repeated revelation when a single beat repair can fix the framing.
`.trim();
}

function sentenceReferencesCharacter(sentence: string, character: string): boolean {
  if (!character) return false;
  const escaped = character.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`, 'i').test(sentence);
}

function sentenceIsPovRediscovery(sentence: string, povCharacter: string): boolean {
  if (!DISCOVERY_VERB_PATTERN.test(sentence)) return false;
  if (!povCharacter) {
    return /\b(?:I|my|me)\b/i.test(sentence);
  }
  return sentenceReferencesCharacter(sentence, povCharacter) || /\b(?:I|my|me)\b/i.test(sentence);
}

function sentenceMatchesKnownFact(sentence: string, fact: string): boolean {
  const tokens = tokenizeFact(fact);
  if (!tokens.length) return false;
  const lower = sentence.toLowerCase();
  const hits = tokens.filter((token) => lower.includes(token)).length;
  return hits >= Math.min(2, tokens.length);
}

function isAllowedNewRevelation(
  sentence: string,
  allowed: PlannedRevelation[],
): PlannedRevelation | undefined {
  return allowed.find((revelation) => {
    if (!sentenceMatchesKnownFact(sentence, revelation.fact) && !DISCOVERY_VERB_PATTERN.test(sentence)) {
      return false;
    }
    return revelation.revealToCharacters.some((character) => sentenceReferencesCharacter(sentence, character));
  });
}

function povKnowsFactAtChapterStart(
  known: CanonicalKnownFact,
  povCharacter: string,
): boolean {
  if (!povCharacter) return true;
  if (known.knownBy.includes('cast')) return true;
  return known.knownBy.some((name) => name.toLowerCase() === povCharacter.toLowerCase());
}

function extractRediscoveryIndicators(sentence: string): string[] {
  const indicators: string[] = [];
  const patterns: Array<[RegExp, string]> = [
    [/\bfor\s+the\s+first\s+time\b/i, 'for the first time'],
    [/\bnever\s+(?:knew|known)\b/i, 'never knew'],
    [/\brealiz(?:ed|es|ing)\b/i, 'realized'],
    [/\bidentif(?:ied|ies|ying)\b/i, 'identified'],
    [/\bdiscover(?:ed|s|ing)?\b/i, 'discovered'],
    [/\blearned\s+that\b/i, 'learned that'],
    [/\bit\s+was\s+(?:an?|the)\b/i, 'it was a/the'],
  ];
  for (const [pattern, label] of patterns) {
    if (pattern.test(sentence)) indicators.push(label);
  }
  return indicators;
}

export function isLegitimateRevelationContext(
  sentence: string,
  knowledge: ReturnType<typeof buildChapterCanonKnowledge>,
): boolean {
  const lower = sentence.toLowerCase();

  if (/\b(?:recall(?:ed|ing)?|remember(?:ed|ing)?)\b/i.test(sentence)) return true;
  if (/\bas\s+(?:he|she|they)\s+had\s+(?:in\s+chapter\s+\d+|already|identified|discovered)\b/i.test(sentence)) {
    return true;
  }
  if (/\b(?:already|previously)\s+(?:knew|known|identified|discovered|understood)\b/i.test(sentence)) return true;
  if (/\b(?:had\s+(?:already\s+)?(?:identified|discovered|learned|known))\b/i.test(sentence) && !EXPLICIT_FIRST_TIME_PATTERN.test(sentence)) {
    return true;
  }
  if (/\b(?:hid|hide|hiding|conceal(?:ed|ing)?|cover(?:ed|ing)?|masked)\b/i.test(sentence)) return true;
  if (/\b(?:explain(?:ed|ing)?|told)\b/i.test(sentence) && knowledge.newRevelationsAllowed.length >= 0) {
    if (/\bto\s+\w+\b/i.test(sentence) || knowledge.newRevelationsAllowed.some((rev) =>
      rev.revealToCharacters.some((name) => sentenceReferencesCharacter(sentence, name)),
    )) {
      return true;
    }
  }
  if (/\b(?:examin(?:ed|ing)|inspect(?:ed|ing)|checked|studied|worked\s+on)\b/i.test(sentence) && !EXPLICIT_FIRST_TIME_PATTERN.test(sentence)) {
    return true;
  }
  if (/\b(?:begun|began|started)\s+(?:broadcast|transmit)/i.test(sentence)) return true;
  if (/\bnew\s+(?:consequence|subsystem|chamber|compartment|array|module)\b/i.test(sentence)) return true;
  if (/\bpreviously\s+unknown\s+subsystem\b/i.test(sentence)) return true;
  if (/\bconfirm(?:ed|ing)?\s+(?:her|his|their)\s+suspicion\b/i.test(sentence)) return true;
  if (/\bwhich\s+\w+\s+already\s+knew\b/i.test(sentence)) return true;
  if (/\bchapter\s+1\b/i.test(lower) && /\b(?:identified|discovered)\b/i.test(sentence) && !EXPLICIT_FIRST_TIME_PATTERN.test(sentence)) {
    return true;
  }

  return false;
}

function isOtherCharacterDiscoverySubject(
  sentence: string,
  knowledge: ReturnType<typeof buildChapterCanonKnowledge>,
): boolean {
  const allowed = isAllowedNewRevelation(sentence, knowledge.newRevelationsAllowed);
  if (!allowed) return false;
  return allowed.revealToCharacters.some((character) =>
    sentenceReferencesCharacter(sentence, character) && !sentenceReferencesCharacter(sentence, knowledge.povCharacter),
  );
}

function isHighConfidenceRediscovery(
  sentence: string,
  known: CanonicalKnownFact,
  knowledge: ReturnType<typeof buildChapterCanonKnowledge>,
): boolean {
  if (!povKnowsFactAtChapterStart(known, knowledge.povCharacter)) return false;
  if (!sentenceIsPovRediscovery(sentence, knowledge.povCharacter)) return false;
  if (isLegitimateRevelationContext(sentence, knowledge)) return false;
  if (isOtherCharacterDiscoverySubject(sentence, knowledge)) return false;
  if (!EXPLICIT_FIRST_TIME_PATTERN.test(sentence) && !/\b(?:identif(?:ied|ies|ying)|realiz(?:ed|es|ing))\b/i.test(sentence)) {
    return false;
  }
  return true;
}

export function resolveBeatIdForSentence(content: string, sentence: string): InspectorAuthoritativeBeatId | undefined {
  const beats = (content || '').split(FOUR_BEAT_SEPARATOR);
  for (let index = 0; index < beats.length; index += 1) {
    const beatText = beats[index] || '';
    if (beatText.includes(sentence)) {
      return `beat-${Math.min(4, index + 1)}` as InspectorAuthoritativeBeatId;
    }
    if (splitSentences(beatText).some((candidate) => candidate === sentence)) {
      return `beat-${Math.min(4, index + 1)}` as InspectorAuthoritativeBeatId;
    }
  }

  const offset = content.indexOf(sentence);
  if (offset < 0) return undefined;
  const separatorPattern = new RegExp(FOUR_BEAT_SEPARATOR.replace(/\*/g, '\\*'), 'g');
  const before = content.slice(0, offset);
  const beatIndex = (before.match(separatorPattern) || []).length;
  const beatNumber = Math.min(4, Math.max(1, beatIndex + 1));
  return `beat-${beatNumber}` as InspectorAuthoritativeBeatId;
}

export function assessSuspectedRepeatedRevelations(
  content: string,
  knowledge: ReturnType<typeof buildChapterCanonKnowledge>,
): SuspectedRepeatedRevelationFinding[] {
  const findings: SuspectedRepeatedRevelationFinding[] = [];
  const beatSections = (content || '').split(FOUR_BEAT_SEPARATOR);

  for (let beatIndex = 0; beatIndex < beatSections.length; beatIndex += 1) {
    const beatText = beatSections[beatIndex] || '';
    const suspectedBeatId = `beat-${Math.min(4, beatIndex + 1)}` as InspectorAuthoritativeBeatId;

    for (const sentence of splitSentences(beatText)) {
      if (!DISCOVERY_VERB_PATTERN.test(sentence)) continue;

      const allowedRevelation = isAllowedNewRevelation(sentence, knowledge.newRevelationsAllowed);
      if (allowedRevelation) continue;

      if (isLegitimateRevelationContext(sentence, knowledge)) {
        continue;
      }

      for (const known of knowledge.knownAtChapterStart) {
        if (!sentenceMatchesKnownFact(sentence, known.statement)) continue;
        if (!sentenceIsPovRediscovery(sentence, knowledge.povCharacter)) continue;

        const allowedDiscoverer = knowledge.newRevelationsAllowed.find((revelation) =>
          revelation.revealToCharacters.some((character) => sentenceReferencesCharacter(sentence, character)),
        )?.revealToCharacters[0];

        findings.push({
          assessment: 'SUSPECTED',
          code: 'SUSPECTED_REPEATED_REVELATION',
          factId: known.factId,
          fact: known.statement,
          povCharacter: knowledge.povCharacter,
          suspectedBeatId,
          sentence,
          reason: `Possible POV rediscovery of established canon: ${known.statement}`,
          rediscoveryIndicators: extractRediscoveryIndicators(sentence),
          allowedDiscoverer,
          highConfidence: isHighConfidenceRediscovery(sentence, known, knowledge),
          legitimate: false,
        });
        break;
      }
    }
  }

  return findings;
}

export function buildSuspectedRevelationInspectorContext(
  findings: SuspectedRepeatedRevelationFinding[],
): string {
  if (!findings.length) return '';

  const lines = findings.map((finding) => {
    const beat = finding.suspectedBeatId ? ` suspectedBeatId=${finding.suspectedBeatId}` : '';
    const discoverer = finding.allowedDiscoverer ? ` allowedDiscoverer=${finding.allowedDiscoverer}` : ' allowedDiscoverer=none';
    const confidence = finding.highConfidence ? ' confidence=high' : ' confidence=low';
    return `- [${finding.code}] factId=${finding.factId}${beat}${discoverer}${confidence} indicators=${finding.rediscoveryIndicators.join('|') || 'none'} sentence="${finding.sentence}"`;
  });

  return `
DETERMINISTIC SUSPECTED REPEATED REVELATION PRECHECK (ADVISORY — NOT A TERMINAL BLOCK):
${lines.join('\n')}
Inspector must confirm or reject each suspicion. Return REPAIR_REQUIRED with reasonCodes ["REPEATED_REVELATION"] and the authoritative beatId only on confirmation.
Legitimate recall, concealment, explanation, new consequences, new subsystems, and other-character first discoveries must PASS.
`.trim();
}

export function inspectorConfirmsRepeatedRevelation(params: {
  reason?: string;
  correction_order?: string;
  beatRepairRequests?: InspectorBeatRepairRequest[];
}): boolean {
  if (params.beatRepairRequests?.some((request) => request.reasonCodes.includes('REPEATED_REVELATION'))) {
    return true;
  }
  const combined = `${params.reason || ''} ${params.correction_order || ''}`.toUpperCase();
  return combined.includes('REPEATED_REVELATION');
}

export function resolveConfirmedRepeatedRevelationRepair(params: {
  suspectedFindings: SuspectedRepeatedRevelationFinding[];
  beatRepairRequests: InspectorBeatRepairRequest[];
  reason?: string;
  correction_order?: string;
}): InspectorBeatRepairRequest | undefined {
  const confirmed = inspectorConfirmsRepeatedRevelation({
    reason: params.reason,
    correction_order: params.correction_order,
    beatRepairRequests: params.beatRepairRequests,
  });
  if (!confirmed) return undefined;

  const fromInspector = params.beatRepairRequests.find((request) =>
    request.reasonCodes.includes('REPEATED_REVELATION'),
  );
  if (fromInspector?.beatId) return fromInspector;

  const highConfidence = params.suspectedFindings.find((finding) => finding.highConfidence && finding.suspectedBeatId);
  if (!highConfidence?.suspectedBeatId) return fromInspector;

  return {
    beatId: highConfidence.suspectedBeatId,
    reasonCodes: ['REPEATED_REVELATION'],
    explanation:
      params.correction_order ||
      `Rewrite ${highConfidence.suspectedBeatId} so ${highConfidence.povCharacter || 'the POV character'} acts on existing knowledge instead of rediscovering: ${highConfidence.fact}`,
  };
}

/** @deprecated Use assessSuspectedRepeatedRevelations — deterministic findings are SUSPECTED only. */
export function detectRepeatedRevelations(
  content: string,
  knowledge: ReturnType<typeof buildChapterCanonKnowledge>,
): Array<{ code: 'SUSPECTED_REPEATED_REVELATION'; factId: string; fact: string; sentence: string; reason: string }> {
  return assessSuspectedRepeatedRevelations(content, knowledge).map((finding) => ({
    code: finding.code,
    factId: finding.factId,
    fact: finding.fact,
    sentence: finding.sentence,
    reason: finding.reason,
  }));
}
